import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ListChecks, MoreHorizontal } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
	DIALOG_CONTENT_CLASS,
	DIALOG_DESCRIPTION_CLASS,
	DIALOG_HEADER_CLASS,
	DIALOG_TITLE_CLASS,
	TicketFieldsPicker
} from '@/components/shared';
import { ROW_MENU_CONTENT, ROW_MENU_ITEM } from '@/components/layouts/menu-styles';
import {
	usePublishedCSCategories,
	useEnabledIntentFilterConfigs,
	useUpsertIntentFilterConfig,
	useBypassTaggingConfigs,
	useSetBypassTagging,
	useEnabledIntentConfigs,
	useUpsertIntentConfig,
	useIntentChecks,
	useAccountFlags,
	INTENT_FILTERING_ENABLED_FLAG
} from '@/hooks';
import {
	useBlockedIntents,
	useSetBlockedIntents,
	useSpamHelpdeskTicketFields,
	useSpamTicketFields,
	useSetSpamTicketFields
} from '@/hooks/use-brand-settings';
import { usePermissions } from '@/hooks/use-permissions';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { SettingsTableRows, StatusTag, type SettingsColumn } from '@/modules/settings';
import { ACTIVE_INTENT_COLUMNS } from './intents-active-columns';
import { IntentChecksSheet } from './intent-checks-sheet';
import type { TicketFieldValue } from '@/types';

interface IntentRow {
	id: string;
	name: string;
	/** Still marked as spam or bypassed, but no longer published: only those two can be undone. */
	unpublished?: boolean;
}

/** Intents › Active: the per-intent switches from three old pages; each saves when flipped. */
export function IntentsActiveTab({ search }: { search: string }) {
	const { can } = usePermissions();
	const canEditBrand = can('edit', 'brand');
	const canEditAutomation = can('edit', 'automation');

	const categoriesQuery = usePublishedCSCategories();
	const filterQuery = useEnabledIntentFilterConfigs();
	const bypassQuery = useBypassTaggingConfigs();
	const automationQuery = useEnabledIntentConfigs();
	const spamQuery = useBlockedIntents();
	const spamConfig = spamQuery.data;
	const { flags, isLoading: flagsLoading, isError: flagsError } = useAccountFlags();

	const upsertFilter = useUpsertIntentFilterConfig();
	const setBypass = useSetBypassTagging();
	const upsertAutomation = useUpsertIntentConfig();
	const setBlocked = useSetBlockedIntents();

	const filteringOn = !!flags[INTENT_FILTERING_ENABLED_FLAG];
	const killSwitchOn = !!flags.intent_automation_kill_switch;

	const processSet = useMemo(() => new Set((filterQuery.data ?? []).map((c) => c.intentId)), [filterQuery.data]);
	const bypassSet = useMemo(() => new Set((bypassQuery.data ?? []).map((c) => c.intentId)), [bypassQuery.data]);
	const autoSet = useMemo(() => new Set((automationQuery.data ?? []).map((c) => c.intentId)), [automationQuery.data]);
	const checksQuery = useIntentChecks();
	const checksByIntent = useMemo(() => new Map((checksQuery.data ?? []).map((entry) => [entry.intentId, entry])), [checksQuery.data]);
	const [checksFor, setChecksFor] = useState<{ id: string; name: string; justEnabled: boolean } | null>(null);
	const blockedIntents = useMemo(() => spamConfig?.blockedIntents ?? [], [spamConfig]);
	const autoCloseIntents = useMemo(() => spamConfig?.spamAutoCloseIntents ?? [], [spamConfig]);

	const rows = useMemo<IntentRow[]>(() => {
		const q = search.trim().toLowerCase();
		const published = (categoriesQuery.data ?? []).map((c) => ({ id: c.id, name: c.name || c.id }));
		const publishedIds = new Set(published.map((row) => row.id));
		const unpublished = [...new Set([...blockedIntents, ...bypassSet])]
			.filter((id) => !publishedIds.has(id))
			.map((id) => ({ id, name: id, unpublished: true }));
		return [...published, ...unpublished]
			.sort((a, b) => a.id.localeCompare(b.id))
			.filter((row) => !q || row.name.toLowerCase().includes(q) || row.id.toLowerCase().includes(q));
	}, [categoriesQuery.data, blockedIntents, bypassSet, search]);

	const run = async (action: Promise<unknown>, failure: string) => {
		try {
			await action;
		} catch (err) {
			toast.error(getApiErrorMessage(err, failure));
		}
	};

	const setSpam = (intentId: string, isSpam: boolean) =>
		run(
			setBlocked.mutateAsync({
				intentIds: isSpam ? [...blockedIntents, intentId] : blockedIntents.filter((id) => id !== intentId),
				// Auto-close only applies to spam intents, so un-marking one also stops it closing tickets.
				autoCloseIntentIds: isSpam ? autoCloseIntents : autoCloseIntents.filter((id) => id !== intentId)
			}),
			'Failed to update spam intents'
		);

	const setAutoClose = (intentId: string, autoClose: boolean) =>
		run(
			setBlocked.mutateAsync({
				intentIds: blockedIntents,
				autoCloseIntentIds: autoClose ? [...autoCloseIntents, intentId] : autoCloseIntents.filter((id) => id !== intentId)
			}),
			'Failed to update auto-close'
		);

	// Switching auto-send on asks what every reply for the intent must do, until it has checks.
	const setAutoSend = async (row: IntentRow, next: boolean) => {
		try {
			await upsertAutomation.mutateAsync({ intentId: row.id, isAutomationEnabled: next });
			if (next && !checksByIntent.has(row.id)) setChecksFor({ id: row.id, name: row.name, justEnabled: true });
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to update automation'));
		}
	};

	const [fieldsFor, setFieldsFor] = useState<IntentRow | null>(null);

	const cells: Record<string, (row: IntentRow) => ReactNode> = {
		name: (row) => (
			<span className='flex min-w-0 items-center gap-2'>
				<span className={cn('truncate', (row.unpublished || (filteringOn && !processSet.has(row.id))) && 'text-gray-700')} title={row.id}>
					{row.name}
				</span>
				{row.unpublished && <StatusTag tone='grey'>Not published</StatusTag>}
			</span>
		),
		skip: (row) => (
			<Checkbox
				checked={bypassSet.has(row.id)}
				disabled={!canEditBrand || setBypass.isPending || (row.unpublished && !bypassSet.has(row.id))}
				aria-label={`Skip pre-processing for ${row.name}`}
				onCheckedChange={(checked) =>
					void run(setBypass.mutateAsync({ intentId: row.id, bypassTagging: checked === true }), 'Failed to update ticket preprocessing bypass')
				}
			/>
		),
		process: (row) => (
			<Switch
				checked={processSet.has(row.id)}
				// Only counts while intent-based filtering is on, as on Ingestion › Intents.
				disabled={!filteringOn || !canEditBrand || upsertFilter.isPending}
				aria-label={`Process ${row.name}`}
				onCheckedChange={(next) =>
					void run(upsertFilter.mutateAsync({ intentId: row.id, isFilteringEnabled: next }), 'Failed to update intent filtering')
				}
			/>
		),
		auto: (row) => (
			<Switch
				checked={autoSet.has(row.id)}
				disabled={killSwitchOn || !canEditAutomation || upsertAutomation.isPending}
				aria-label={`Auto-send ${row.name}`}
				onCheckedChange={(next) => void setAutoSend(row, next)}
			/>
		),
		checks: (row) => {
			const entry = checksByIntent.get(row.id);
			const count = entry
				? entry.requiredTools.length +
					entry.forbiddenTools.length +
					entry.sources.sopIds.length +
					entry.sources.workflowIds.length +
					entry.sources.knowledgeIds.length
				: 0;
			return (
				<button
					type='button'
					onClick={() => setChecksFor({ id: row.id, name: row.name, justEnabled: false })}
					title='What every reply Kim sends on its own for this intent must do'
					aria-label={`Checks for ${row.name}`}
					className={cn(
						'flex h-6 cursor-pointer items-center gap-1 rounded-full px-2 text-[11px] leading-4 transition-colors',
						count > 0 ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100' : 'text-gray-800 hover:bg-gray-100'
					)}
				>
					<ListChecks className='size-3.5' />
					{count > 0 ? `${count} ${count === 1 ? 'check' : 'checks'}` : 'Checks'}
				</button>
			);
		},
		// With playbooks on, saving these rewrites the spam playbooks made from them (the page looks the same).
		spam: (row) => (
			<div className='flex items-center gap-1'>
				<Checkbox
					checked={blockedIntents.includes(row.id)}
					disabled={!canEditAutomation || setBlocked.isPending || (row.unpublished && !blockedIntents.includes(row.id))}
					aria-label={`Mark ${row.name} as spam`}
					onCheckedChange={(checked) => void setSpam(row.id, checked === true)}
				/>
				{/* Auto-close and its ticket fields only apply to a spam intent. */}
				{blockedIntents.includes(row.id) && (
					<SpamRowMenu
						row={row}
						autoCloses={autoCloseIntents.includes(row.id)}
						disabled={!canEditAutomation || setBlocked.isPending}
						onAutoCloseChange={(next) => void setAutoClose(row.id, next)}
						onOpenTicketFields={() => setFieldsFor(row)}
					/>
				)}
			</div>
		)
	};
	// An unpublished row offers only what can be undone: skipping pre-processing and marking as spam.
	const columns: SettingsColumn<IntentRow>[] = ACTIVE_INTENT_COLUMNS.map((head) => ({
		...head,
		cell: (row) => (row.unpublished && !['name', 'skip', 'spam'].includes(head.id) ? null : cells[head.id]?.(row))
	}));

	// Spam saves the whole list, so it must be loaded before any row can change it.
	const isLoading =
		categoriesQuery.isLoading || filterQuery.isLoading || bypassQuery.isLoading || automationQuery.isLoading || spamQuery.isLoading || flagsLoading;
	// Flags decide what is locked; without them the switches would claim automation and filtering are off.
	const isError =
		categoriesQuery.isError || filterQuery.isError || bypassQuery.isError || automationQuery.isError || spamQuery.isError || flagsError;

	return (
		<>
			{(!filteringOn || killSwitchOn) && !isLoading && !isError && (
				<div className='flex flex-col gap-1 border-b border-gray-300 bg-amber-50 px-4 py-2 text-[12px] leading-4 text-amber-800'>
					{!filteringOn && (
						<p>
							Intent-based filtering is off, so Kim processes every intent and Process Intent is locked. Turn it on in{' '}
							<Link to='/settings/ingestion?tab=intents' className='font-medium underline'>
								Ingestion › Intents
							</Link>
							.
						</p>
					)}
					{killSwitchOn && (
						<p>
							Automation is off, so Auto-send is locked. Turn it on in{' '}
							<Link to='/settings/automation' className='font-medium underline'>
								Automation
							</Link>
							.
						</p>
					)}
				</div>
			)}
			<SettingsTableRows
				columns={columns}
				rows={isError ? [] : rows}
				rowKey={(row) => row.id}
				isLoading={isLoading}
				empty={isError ? 'Failed to load intents.' : search.trim() ? `No intents match "${search}"` : 'No published intents found.'}
			/>
			<SpamTicketFieldsDialog intent={fieldsFor} onClose={() => setFieldsFor(null)} />
			<IntentChecksSheet
				intent={checksFor}
				saved={checksFor ? checksByIntent.get(checksFor.id) : undefined}
				justEnabled={checksFor?.justEnabled ?? false}
				readOnly={!canEditAutomation}
				onClose={() => setChecksFor(null)}
			/>
		</>
	);
}

interface SpamRowMenuProps {
	row: IntentRow;
	autoCloses: boolean;
	disabled: boolean;
	onAutoCloseChange: (next: boolean) => void;
	onOpenTicketFields: () => void;
}

/** Spam options for one intent: auto-close its tickets, and the Gorgias fields that close fills in. */
function SpamRowMenu({ row, autoCloses, disabled, onAutoCloseChange, onOpenTicketFields }: SpamRowMenuProps) {
	const { data: helpdeskFields } = useSpamHelpdeskTicketFields();
	const { data: ticketFieldsByIntent = {} } = useSpamTicketFields();
	const saved = ticketFieldsByIntent[row.id] ?? [];
	const missingRequired = (helpdeskFields?.fields ?? []).some((field) => field.required && !saved.some((entry) => entry.fieldId === field.id));
	const offersFields = autoCloses && (Boolean(helpdeskFields?.available && helpdeskFields.fields.length) || Boolean(helpdeskFields?.needsReconnect));
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type='button'
					aria-label={`Spam options for ${row.name}`}
					className='flex size-6 cursor-pointer items-center justify-center rounded-full text-gray-800 hover:bg-gray-100'
				>
					<MoreHorizontal className='size-3.5' />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align='end' className={cn(ROW_MENU_CONTENT, 'min-w-[220px]')}>
				<DropdownMenuLabel className='px-2.5 text-[12px] font-medium text-gray-700'>Spam</DropdownMenuLabel>
				<DropdownMenuCheckboxItem
					checked={autoCloses}
					disabled={disabled}
					onCheckedChange={(checked) => onAutoCloseChange(checked === true)}
					className={cn(ROW_MENU_ITEM, 'pl-8')}
				>
					Auto-close its tickets
				</DropdownMenuCheckboxItem>
				{offersFields && (
					<DropdownMenuItem onClick={onOpenTicketFields} className={ROW_MENU_ITEM}>
						Ticket fields to set on close…
						{missingRequired && <span className='ml-auto text-[11px] text-red-500'>Needs values</span>}
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

/** Gorgias will not close a ticket while a required ticket field is empty, so each auto-closing spam intent carries values. */
function SpamTicketFieldsDialog({ intent, onClose }: { intent: IntentRow | null; onClose: () => void }) {
	const { data: helpdeskFields } = useSpamHelpdeskTicketFields();
	const { data: ticketFieldsByIntent = {} } = useSpamTicketFields();
	const setTicketFields = useSetSpamTicketFields();
	const fields = (helpdeskFields?.available && helpdeskFields.fields) || [];

	const handleChange = async (updated: TicketFieldValue[]) => {
		if (!intent) return;
		try {
			await setTicketFields.mutateAsync({ intentId: intent.id, ticketFields: updated });
			toast.success('Ticket fields updated');
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to update ticket fields'));
		}
	};

	return (
		<Dialog open={intent !== null} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className={cn(DIALOG_CONTENT_CLASS, 'max-w-[520px] bg-white')}>
				<DialogHeader className={DIALOG_HEADER_CLASS}>
					<DialogTitle className={DIALOG_TITLE_CLASS}>Ticket fields for {intent?.name}</DialogTitle>
					<DialogDescription className={DIALOG_DESCRIPTION_CLASS}>
						{helpdeskFields?.needsReconnect
							? 'Gorgias will not close a ticket while a required ticket field is empty. Reconnect Gorgias so its ticket fields can be set here.'
							: 'Gorgias will not close a ticket while a required ticket field is empty. These values are filled in when this intent auto-closes a ticket, on every brand.'}
					</DialogDescription>
				</DialogHeader>
				{intent && fields.length > 0 && (
					<div className='px-5 pb-5'>
						<TicketFieldsPicker
							fields={fields}
							value={ticketFieldsByIntent[intent.id] ?? []}
							onChange={(updated) => void handleChange(updated)}
							disabled={setTicketFields.isPending}
						/>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
